import { getToolName, isToolUIPart, type UIMessage } from "ai";

type LegacyToolInvocationPart = {
  type: "tool-invocation";
  toolInvocation?: {
    toolCallId?: string;
    toolName?: string;
    args?: unknown;
    input?: unknown;
    result?: unknown;
  };
};

export type NormalizedToolCall = {
  toolName: string;
  callId?: string;
  input?: unknown;
  output?: unknown;
  state?: string;
};

export function getMessageText(message: UIMessage): string {
  if (message.parts && message.parts.length > 0) {
    const textParts = message.parts
      .map((part) => {
        if (part.type === "text" || part.type === "reasoning") {
          return part.text;
        }

        if (isLegacyToolInvocation(part)) {
          const result = part.toolInvocation?.result;
          return typeof result === "string" ? result : "";
        }

        return "";
      })
      .filter((text) => Boolean(text && text.trim()))
      .map((text) => text.trim());

    if (textParts.length > 0) {
      return textParts.join("\n\n");
    }
  }

  const maybeContent = (message as { content?: unknown }).content;
  if (typeof maybeContent === "string") {
    return maybeContent.trim();
  }

  return "";
}

export function getToolCalls(message: UIMessage): NormalizedToolCall[] {
  const parts = (message.parts ?? []) as unknown[];
  const calls: NormalizedToolCall[] = [];

  for (const part of parts) {
    if (isToolUIPart(part as any)) {
      calls.push({
        toolName: String(getToolName(part as any)),
        callId: (part as any).toolCallId,
        input: (part as any).input,
        output: (part as any).output,
        state: (part as any).state,
      });
      continue;
    }

    if (isLegacyToolInvocation(part)) {
      const invocation = part.toolInvocation;
      if (invocation?.toolName) {
        calls.push({
          toolName: invocation.toolName,
          callId: invocation.toolCallId,
          input: invocation.args ?? invocation.input,
          output: invocation.result,
        });
      }
    }
  }

  return calls;
}

export function getMessageTimestamp(message: UIMessage): number {
  const createdAt = (message as { createdAt?: string | number | Date }).createdAt;
  return createdAt ? +new Date(createdAt) : Date.now();
}

function isLegacyToolInvocation(part: unknown): part is LegacyToolInvocationPart {
  return Boolean(part) && (part as LegacyToolInvocationPart)?.type === "tool-invocation";
}
